import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AccountInfo, InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { Button, Content, Header, Layout, Popover, Segmented } from "./assets/imports/antdComponents";
import RoutesConfig from "./config/routesConfig";
import { adminTabs } from "./config/menuConfig";
import AuthScreen from "./components/AutenticatingComp";
import KccErrorBoundary from "./components/KccErrorBoundary";
import DynamicLogo from "./components/DynamicLogo";
import { getCognitoIdentity } from "./services/cognitoAuth";
import { signOutFromApp } from "./services/authorization";
import "./AppContent.css";

// Resolve the signed-in user's display name across IDPs: MSAL's `name` when
// present, else the token claims Cognito/Entra populate (given/family name,
// preferred_username, email), else the raw username.
const accountDisplayName = (account?: AccountInfo): string => {
	if (!account) return "";
	const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>;
	const str = (value: unknown): string => (typeof value === "string" ? value.trim() : "");
	const given = str(claims.given_name);
	const family = str(claims.family_name);
	return (
		str(account.name) ||
		[given, family].filter(Boolean).join(" ") ||
		str(claims.name) ||
		str(claims.preferred_username) ||
		str(claims.email) ||
		str(account.username)
	);
};

// Two-letter initials from a display name or email.
const toInitials = (source: string): string => {
	const trimmed = source.trim();
	if (!trimmed) return "";
	const local = trimmed.includes("@") ? trimmed.split("@")[0] : trimmed;
	const parts = local.split(/[.\s_-]+/).filter(Boolean);
	const first = parts[0]?.[0] ?? "";
	const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
	return (first + last).toUpperCase();
};

const AppContent: React.FC = () => {
	const { t } = useTranslation();
	const { accounts, inProgress } = useMsal();
	const navigate = useNavigate();
	const location = useLocation();

	// The app authenticates via Cognito (Amplify), so MSAL `accounts` is usually
	// empty here — resolve the display name/email from the Cognito id-token claims,
	// falling back to an MSAL account when that IDP is the active one.
	const [cognitoName, setCognitoName] = React.useState("");
	const [cognitoEmail, setCognitoEmail] = React.useState("");
	const [accountOpen, setAccountOpen] = React.useState(false);
	const [signingOut, setSigningOut] = React.useState(false);
	React.useEffect(() => {
		let alive = true;
		getCognitoIdentity()
			.then((identity) => {
				if (alive && identity) {
					setCognitoName(identity.name);
					setCognitoEmail(identity.email);
				}
			})
			.catch(() => undefined);
		return () => {
			alive = false;
		};
	}, []);

	const handleSignOut = async () => {
		setSigningOut(true);
		try {
			await signOutFromApp();
		} catch {
			// Sign-out redirect failed — re-enable so the user can retry.
			setSigningOut(false);
		}
	};

	if (inProgress !== InteractionStatus.None) {
		return <AuthScreen profile={true} />;
	}

	const activeKey = adminTabs.find((tab) => location.pathname.startsWith(tab.path))?.key ?? adminTabs[0].key;

	const redirectToHome = () => navigate(adminTabs[0].path);

	const handleNavigationChange = (key: string | number) => {
		const tab = adminTabs.find((item) => item.key === key);
		if (tab) navigate(tab.path);
	};

	const displayName = accountDisplayName(accounts[0]) || cognitoName;
	const initials = toInitials(displayName);

	return (
		<Layout style={{ height: "100vh", overflow: "hidden", background: "var(--bg-base-color)" }}>
			<div className="scale-baseline-100 admin-shell">
				<Header className="top-navbar flex justify-between items-center bg-[--neutral-white-opacity-90]">
					<div className="top-navbar-left flex items-center gap-16">
						<div className="flex items-center">
							<span className="flex items-center">
								<DynamicLogo redirectToHome={redirectToHome} />
							</span>
							<div className="ml-7 mr-5">
								<Segmented
									className="top-navbar-navigation"
									options={adminTabs.map((tab) => ({ label: tab.label, value: tab.key }))}
									value={activeKey}
									onChange={handleNavigationChange}
								/>
							</div>
						</div>
					</div>

					<div className="top-navbar-actions">
						<Popover
							trigger="click"
							placement="bottomRight"
							open={accountOpen}
							onOpenChange={setAccountOpen}
							rootClassName="kcc-account-popover"
							content={
								<div className="kcc-account-pop">
									<div className="kcc-account-pop__id">
										<span className="kcc-account-pop__name">{displayName || t("Superadmin")}</span>
										{cognitoEmail && <span className="kcc-account-pop__email">{cognitoEmail}</span>}
									</div>
									<Button
										className="kcc-account-pop__signout"
										block
										loading={signingOut}
										onClick={handleSignOut}
										icon={<i className="material-symbols-outlined">logout</i>}>
										{t("Sign out")}
									</Button>
								</div>
							}>
							<span
								className="top-navbar-avatar"
								role="button"
								tabIndex={0}
								title={displayName || accounts[0]?.username || t("Superadmin")}>
								{initials || <i className="material-symbols-outlined">person</i>}
							</span>
						</Popover>
					</div>
				</Header>
				<Content className="admin-content" style={{ padding: "1.5rem" }}>
					{/* Keyed by path: navigating away resets a crashed screen instead of
					    pinning the whole app on the error fallback (arch review P0-1). */}
					<KccErrorBoundary key={location.pathname}>
						<RoutesConfig />
					</KccErrorBoundary>
				</Content>
			</div>
		</Layout>
	);
};

export default AppContent;
